<template>
  <div :class="showCard ? 'card' : ''">
    <div :class="showCard ? 'card-body p-3' : ''">
      <div v-if="isFreeUpgrade" class="alert alert-info mb-3">
        <font-awesome-icon icon="info-circle" class="me-2" />
        <strong>Kostenloses Upgrade:</strong> Die Rechnungsdaten sind optional, da du einen gültigen Gutschein verwendest.
      </div>
      <form @submit.prevent="proceedToPayment">
        <div class="row g-2">
          <div class="col-md-6">
            <label class="form-label">Vorname <span v-if="!isFreeUpgrade">*</span></label>
            <input 
              v-model="localBillingData.firstName"
              type="text" 
              class="form-control" 
              :required="!isFreeUpgrade"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">Nachname <span v-if="!isFreeUpgrade">*</span></label>
            <input 
              v-model="localBillingData.lastName"
              type="text" 
              class="form-control" 
              :required="!isFreeUpgrade"
            />
          </div>
          <div class="col-12">
            <label class="form-label">Unternehmen (optional)</label>
            <input 
              v-model="localBillingData.company"
              type="text" 
              class="form-control" 
            />
          </div>
          <div class="col-md-8">
            <label class="form-label">Straße <span v-if="!isFreeUpgrade">*</span></label>
            <input 
              v-model="localBillingData.street"
              type="text" 
              class="form-control" 
              :required="!isFreeUpgrade"
            />
          </div>
          <div class="col-md-4">
            <label class="form-label">Hausnummer <span v-if="!isFreeUpgrade">*</span></label>
            <input 
              v-model="localBillingData.number"
              type="text" 
              class="form-control" 
              :required="!isFreeUpgrade"
            />
          </div>
          <div class="col-md-4">
            <label class="form-label">PLZ <span v-if="!isFreeUpgrade">*</span></label>
            <input 
              v-model="localBillingData.zip"
              type="text" 
              class="form-control" 
              :required="!isFreeUpgrade"
            />
          </div>
          <div class="col-md-8">
            <label class="form-label">Stadt <span v-if="!isFreeUpgrade">*</span></label>
            <input 
              v-model="localBillingData.city"
              type="text" 
              class="form-control" 
              :required="!isFreeUpgrade"
            />
          </div>
          <div class="col-12">
            <label class="form-label">Land <span v-if="!isFreeUpgrade">*</span></label>
            <select v-model="localBillingData.country" class="form-select" :required="!isFreeUpgrade">
              <option value="">Land auswählen</option>
              <option value="Deutschland">Deutschland</option>
            </select>
          </div>
          <div class="col-12">
            <label class="form-label">E-Mail-Adresse <span v-if="!isFreeUpgrade">*</span></label>
            <input 
              v-model="localBillingData.email"
              type="email" 
              class="form-control" 
              :required="!isFreeUpgrade"
            />
            <div class="form-text">Die Zahlungsaufforderung bzw. Rechnung wird an diese E-Mail-Adresse gesendet.</div>
          </div>
          <div v-if="showTaxFields" class="col-md-6">
            <label class="form-label">Steuernummer (optional)</label>
            <input 
              v-model="localBillingData.taxId"
              type="text" 
              class="form-control" 
            />
          </div>
          <div v-if="showTaxFields" class="col-md-6">
            <label class="form-label">USt-IdNr. (optional)</label>
            <input 
              v-model="localBillingData.vatId"
              type="text" 
              class="form-control" 
            />
          </div>
          <div v-if="showSaveOption" class="col-12">
            <div class="form-check">
              <input 
                v-model="localBillingData.saveForFuture"
                class="form-check-input" 
                type="checkbox" 
                id="saveForFuture"
              />
              <label class="form-check-label" for="saveForFuture">
                Speichere Rechnungsdaten für zukünftige Einkäufe
              </label>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick } from 'vue'
import { useBillingData } from '../composables/useBillingData'

const props = defineProps({
  isFreeUpgrade: {
    type: Boolean,
    default: false
  },
  showTaxFields: {
    type: Boolean,
    default: true
  },
  showSaveOption: {
    type: Boolean,
    default: true
  },
  showCard: {
    type: Boolean,
    default: true
  },
  billingData: {
    type: Object,
    required: false,
    default: () => ({
      firstName: '',
      lastName: '',
      company: '',
      street: '',
      number: '',
      zip: '',
      city: '',
      country: 'Deutschland',
      email: '',
      vatId: '',
      taxId: '',
      saveForFuture: true
    })
  }
})

const emit = defineEmits(['proceed-to-payment', 'billing-data-changed'])

const { billingData, loadLastBillingData } = useBillingData()

const defaultBillingData = {
  firstName: '',
  lastName: '',
  company: '',
  street: '',
  number: '',
  zip: '',
  city: '',
  country: 'Deutschland',
  email: '',
  vatId: '',
  taxId: '',
  saveForFuture: true
}

const billingFields = Object.keys(defaultBillingData)

const createBillingData = (data = {}) => ({
  ...defaultBillingData,
  ...data
})

const isSameBillingData = (a = {}, b = {}) => {
  return billingFields.every((field) => {
    const valueA = a?.[field] ?? defaultBillingData[field]
    const valueB = b?.[field] ?? defaultBillingData[field]
    return valueA === valueB
  })
}

const localBillingData = ref(
  createBillingData(
    props.billingData && Object.keys(props.billingData).length
      ? props.billingData
      : billingData.value
  )
)

const isSyncingFromExternal = ref(false)
const lastEmittedBillingData = ref(createBillingData(localBillingData.value))

const updateLocalBillingData = async (data, { skipEmit = false } = {}) => {
  const normalized = createBillingData(data)
  if (isSameBillingData(normalized, localBillingData.value)) {
    if (skipEmit) {
      lastEmittedBillingData.value = { ...normalized }
    }
    return
  }

  if (skipEmit) {
    isSyncingFromExternal.value = true
  }

  localBillingData.value = normalized

  if (skipEmit) {
    lastEmittedBillingData.value = { ...normalized }
    await nextTick()
    isSyncingFromExternal.value = false
  }
}

// Load billing data on mount if not provided via props
onMounted(async () => {
  if (!props.billingData || Object.keys(props.billingData).length === 0 || !props.billingData.firstName) {
    await loadLastBillingData()
    await updateLocalBillingData(billingData.value)
  }
})

// Watch for changes in props and update local data
watch(() => props.billingData, (newData) => {
  if (newData) {
    updateLocalBillingData(newData, { skipEmit: true })
  }
}, { deep: true })

watch(localBillingData, (newData) => {
  if (isSyncingFromExternal.value) {
    return
  }

  const normalized = createBillingData(newData)

  if (isSameBillingData(normalized, lastEmittedBillingData.value)) {
    return
  }

  lastEmittedBillingData.value = { ...normalized }
  emit('billing-data-changed', normalized)
}, { deep: true })

const proceedToPayment = () => {
  const data = localBillingData.value
  const isValid = !!(
    data.firstName &&
    data.lastName &&
    data.street &&
    data.number &&
    data.zip &&
    data.city &&
    data.country &&
    data.email &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)
  )
  
  if (isValid) {
    // Emit current data before proceeding
    const payload = createBillingData(localBillingData.value)
    lastEmittedBillingData.value = { ...payload }
    emit('billing-data-changed', payload)
    emit('proceed-to-payment')
  }
}
</script>
